import {
  EXPENSE_DOC_KIND_LABELS,
  EXPENSE_PAYMENT_METHOD_LABELS,
  EXPENSE_STATUS_LABELS,
  EXPENSE_STATUSES,
  type Expense,
  type ExpenseStatus,
  expensePaymentMethodSchema,
  expenseStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Input,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Coins, FilePlus2 } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Amount, useCompanyStations } from "../../../features/accounting/shared.js";
import { companySearchShape } from "../../../features/accounting/company.js";
import { CounterpartyIdCombobox } from "../../../features/counterparties/counterparty-combobox.js";
import { useExpenseCategories, useExpenses } from "../../../features/expenses/queries.js";
import {
  ExpenseLedgerBadge,
  ExpensesPage,
  ExpenseStatusBadge,
  flattenCategories,
  useExpensePermissions,
} from "../../../features/expenses/shared.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  status: expenseStatusSchema.optional().catch(undefined),
  categoryId: z.string().uuid().optional().catch(undefined),
  counterpartyId: z.string().uuid().optional().catch(undefined),
  stationId: z.string().uuid().optional().catch(undefined),
  paymentMethod: expensePaymentMethodSchema.optional().catch(undefined),
  from: date,
  to: date,
  q: z.string().max(100).optional().catch(undefined),
});
type ExpenseSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/giderler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ExpenseListPage,
});

function ExpenseListPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const permissions = useExpensePermissions();
  return (
    <ExpensesPage
      title={t("menu.expenses")}
      description={t("expenses.list.description")}
      actions={
        permissions.canManage ? (
          <Button asChild>
            <Link to="/giderler/yeni">
              <FilePlus2 aria-hidden="true" />
              {t("expenses.list.new")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) => <ExpenseList companyId={companyId} search={search} />}
    </ExpensesPage>
  );
}

function ExpenseList({ companyId, search }: { companyId: string; search: ExpenseSearch }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const shape = useTenantShape();
  const stations = useCompanyStations(companyId);
  const categories = useExpenseCategories(companyId);
  const [searchText, setSearchText] = useState(search.q ?? "");

  const setFilters = (patch: Partial<ExpenseSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const filters = {
    ...(search.status ? { status: search.status } : {}),
    ...(search.categoryId ? { categoryId: search.categoryId } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
    ...(search.stationId ? { stationId: search.stationId } : {}),
    ...(search.paymentMethod ? { paymentMethod: search.paymentMethod } : {}),
    ...(search.from ? { dateFrom: search.from } : {}),
    ...(search.to ? { dateTo: search.to } : {}),
    ...(search.q ? { search: search.q } : {}),
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useExpenses(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<Expense>[]>(
    () => [
      {
        id: "expenseNo",
        header: t("expenses.fields.expenseNo"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/giderler/$id"
            params={{ id: row.id }}
            className="font-medium underline underline-offset-4"
          >
            {row.expenseNo ?? t("expenses.list.draftNo")}
          </Link>
        ),
        exportValue: (row) => row.expenseNo,
      },
      {
        id: "docDate",
        header: t("expenses.fields.docDate"),
        cell: (row) => formatDate(row.docDate),
        exportValue: (row) => row.docDate,
      },
      {
        id: "docKind",
        header: t("expenses.fields.docKind"),
        cell: (row) => EXPENSE_DOC_KIND_LABELS[row.docKind],
        exportValue: (row) => EXPENSE_DOC_KIND_LABELS[row.docKind],
      },
      {
        id: "category",
        header: t("expenses.fields.category"),
        cell: (row) => row.categoryName,
        exportValue: (row) => row.categoryName,
      },
      {
        id: "dimension",
        header: t("expenses.fields.dimension"),
        cell: (row) =>
          row.allocationKeyId
            ? t("expenses.list.allocated")
            : ((stations.data ?? []).find((station) => station.id === row.stationId)?.name ??
              t("expenses.fields.wholeCompany")),
      },
      {
        id: "netAmount",
        header: t("expenses.fields.netAmount"),
        align: "right",
        cell: (row) => <Amount value={row.netAmount} />,
        exportValue: (row) => row.netAmount,
      },
      {
        id: "vatAmount",
        header: t("expenses.fields.vatAmount"),
        align: "right",
        defaultVisible: false,
        cell: (row) => <Amount value={row.vatAmount} />,
        exportValue: (row) => row.vatAmount,
      },
      {
        id: "payableAmount",
        header: t("expenses.fields.payableAmount"),
        align: "right",
        cell: (row) => <Amount value={row.payableAmount} />,
        exportValue: (row) => row.payableAmount,
      },
      {
        id: "paymentMethod",
        header: t("expenses.fields.paymentMethod"),
        cell: (row) => EXPENSE_PAYMENT_METHOD_LABELS[row.paymentMethod],
        exportValue: (row) => EXPENSE_PAYMENT_METHOD_LABELS[row.paymentMethod],
      },
      {
        id: "status",
        header: t("common.status"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-1">
            <ExpenseStatusBadge status={row.status} />
            <ExpenseLedgerBadge status={row.ledgerStatus} />
          </span>
        ),
        exportValue: (row) => EXPENSE_STATUS_LABELS[row.status],
      },
    ],
    [t, stations.data],
  );

  const tabs: Array<ExpenseStatus | "ALL"> = ["ALL", ...EXPENSE_STATUSES];

  return (
    <Tabs
      className="grid gap-4"
      value={search.status ?? "ALL"}
      onValueChange={(next) => setFilters({ status: next === "ALL" ? undefined : (next as ExpenseStatus) })}
    >
      <TabsList aria-label={t("expenses.list.statusTabs")}>
        {tabs.map((tab) => (
          <TabsTrigger key={tab} value={tab}>
            {tab === "ALL" ? t("common.all") : EXPENSE_STATUS_LABELS[tab]}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Etkin sekmenin paneli: tetikleyicinin `aria-controls` hedefi var olmalı (axe: aria-valid-attr-value). */}
      <TabsContent value={search.status ?? "ALL"} className="pt-0">
        <DataTable
          caption={t("menu.expenses")}
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
          emptyState={
            <EmptyState
              headingLevel={2}
              icon={<Coins />}
              title={t("expenses.list.emptyTitle")}
              description={t("expenses.list.emptyDescription")}
            />
          }
          filters={
            <>
              <FormField label={t("expenses.fields.category")} className="w-56">
                {(control) => (
                  <Select
                    {...control}
                    value={search.categoryId ?? ""}
                    onChange={(event) => setFilters({ categoryId: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {flattenCategories(categories.data ?? []).map(({ category, depth }) => (
                      <option key={category.id} value={category.id}>
                        {" ".repeat(depth * 3)}
                        {category.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("expenses.fields.counterparty")} className="w-64">
                {(control) => (
                  <CounterpartyIdCombobox
                    {...control}
                    companyId={companyId}
                    counterpartyId={search.counterpartyId ?? null}
                    onChange={(id) => setFilters({ counterpartyId: id ?? undefined })}
                  />
                )}
              </FormField>
              {shape.isSingleStation ? null : (
                <FormField label={t("expenses.fields.station")} className="w-48">
                  {(control) => (
                    <Select
                      {...control}
                      value={search.stationId ?? ""}
                      onChange={(event) => setFilters({ stationId: event.target.value || undefined })}
                    >
                      <option value="">{t("common.all")}</option>
                      {(stations.data ?? []).map((station) => (
                        <option key={station.id} value={station.id}>
                          {station.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormField>
              )}
              <FormField label={t("expenses.fields.paymentMethod")} className="w-44">
                {(control) => (
                  <Select
                    {...control}
                    value={search.paymentMethod ?? ""}
                    onChange={(event) =>
                      setFilters({
                        paymentMethod:
                          event.target.value === ""
                            ? undefined
                            : expensePaymentMethodSchema.parse(event.target.value),
                      })
                    }
                  >
                    <option value="">{t("common.all")}</option>
                    {expensePaymentMethodSchema.options.map((method) => (
                      <option key={method} value={method}>
                        {EXPENSE_PAYMENT_METHOD_LABELS[method]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("expenses.list.from")} className="w-40">
                {(control) => (
                  <DateInput
                    {...control}
                    value={search.from ?? null}
                    onValueChange={(value) => setFilters({ from: value ?? undefined })}
                  />
                )}
              </FormField>
              <FormField label={t("expenses.list.to")} className="w-40">
                {(control) => (
                  <DateInput
                    {...control}
                    value={search.to ?? null}
                    onValueChange={(value) => setFilters({ to: value ?? undefined })}
                  />
                )}
              </FormField>
              <FormField label={t("common.search")} className="w-56">
                {(control) => (
                  <Input
                    {...control}
                    type="search"
                    value={searchText}
                    placeholder={t("expenses.list.searchPlaceholder")}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                )}
              </FormField>
            </>
          }
          pagination={{
            hasPrevious: pages.hasPrevious,
            hasNext: list.data?.page.hasMore ?? false,
            onPrevious: pages.previous,
            onNext: () => pages.next(list.data?.page.nextCursor),
            pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
          }}
        />
      </TabsContent>
    </Tabs>
  );
}
